import * as THREE from 'three';
import { CARD_WIDTH, CARD_HEIGHT, CARD_THICKNESS } from '../cardMesh';

// ── Charge. Pre-rendered lightning bolts ────────────────────────
//
// Production-grade lightning effect using the canonical AAA
// technique:
//
//   1. Pre-render lightning bolt textures on canvas using recursive
//      midpoint-displacement (the algorithm that actually models how
//      ionized air paths form. Every game with proper lightning
//      uses this, from Dota 2 to Hearthstone). Multiple bolt
//      variations rendered into a single texture atlas.
//
//   2. Each bolt drawn THREE times with decreasing line width and
//      increasing shadow blur: wide purple bloom → medium cyan glow
//      → thin pure-white core. The bright-core-with-coloured-halo
//      look is baked into the texture rather than computed in shader.
//
//   3. Several instanced billboard quads sample the texture atlas,
//      each with its own random position / rotation / scale /
//      flash-cycle phase. Per-instance lifecycle is `instant ON →
//      fade out over ~200ms → wait ~2s → next flash`. The storm
//      cadence that makes the eye read this as electricity rather
//      than animation.
//
//   4. Subtle persistent base glow underneath so the card stays
//      visually "charged" between flashes.

const RISE_HEIGHT_OFFSET = 0.06;

// ── Bolt instance constants ──────────────────────────────────────
const BOLT_COUNT = 8;
const BOLT_PERIOD = 1.8;       // seconds between flashes for one bolt
const BOLT_LIFETIME = 0.28;    // seconds visible per flash
// Bolts can land anywhere within ~80% of the card area.
const BOLT_SPAWN_RADIUS_X = CARD_WIDTH * 0.40;
const BOLT_SPAWN_RADIUS_Y = CARD_HEIGHT * 0.40;
// Quad size for each bolt (scaled randomly per-instance below).
const BOLT_SIZE = Math.max(CARD_WIDTH, CARD_HEIGHT) * 0.55;

// Atlas grid: 2×2 = 4 bolt variations sampled by per-instance seed.
const ATLAS_GRID = 2;
const ATLAS_TILE_PX = 256;

let _sharedBoltAtlas = null;

/**
 * Recursive midpoint-displacement to generate a fractal lightning
 * polyline from `a` to `b`. Returns an array of line segments
 * (`[[x1,y1],[x2,y2]]`). At each level, the midpoint is offset
 * perpendicular to the segment by a shrinking random amount, and
 * a branch can split off with small probability.
 */
function generateBoltSegments(a, b, depth, maxOffset) {
  if (depth <= 0) return [[a, b]];

  const mx = (a[0] + b[0]) / 2;
  const my = (a[1] + b[1]) / 2;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len = Math.hypot(dx, dy);
  if (len < 0.001) return [[a, b]];

  // Perpendicular unit vector × random signed offset.
  const offset = (Math.random() - 0.5) * 2 * maxOffset;
  const perpX = (-dy / len) * offset;
  const perpY = ( dx / len) * offset;

  const mid = [mx + perpX, my + perpY];
  const next = maxOffset * 0.55;

  const segs = [];
  segs.push(...generateBoltSegments(a, mid, depth - 1, next));
  segs.push(...generateBoltSegments(mid, b, depth - 1, next));

  // 35% chance of branching off the midpoint with a smaller, shorter
  // sub-bolt. Branches stop recursing earlier than the trunk so
  // they're visibly thinner / less dramatic.
  if (Math.random() < 0.35 && depth > 2) {
    const branchLen = (0.4 + Math.random() * 0.4);
    const branchEnd = [
      mid[0] + perpX * 2.2 + (Math.random() - 0.5) * maxOffset * 0.8,
      mid[1] + perpY * 2.2 + (dy / len) * branchLen * len * 0.5,
    ];
    segs.push(...generateBoltSegments(mid, branchEnd, depth - 2, next * 0.5));
  }

  return segs;
}

/**
 * Stroke a polyline using the current canvas context.
 */
function strokeSegments(ctx, segments) {
  ctx.beginPath();
  for (let i = 0; i < segments.length; i++) {
    const [a, b] = segments[i];
    ctx.moveTo(a[0], a[1]);
    ctx.lineTo(b[0], b[1]);
  }
  ctx.stroke();
}

/**
 * Build a 2×2 atlas of lightning bolts. Each tile is independently
 * generated, so instances sampling different tiles get visually
 * distinct bolts.
 */
function getBoltAtlas() {
  if (_sharedBoltAtlas) return _sharedBoltAtlas;

  const tile = ATLAS_TILE_PX;
  const size = tile * ATLAS_GRID;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');

  ctx.clearRect(0, 0, size, size);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (let gy = 0; gy < ATLAS_GRID; gy++) {
    for (let gx = 0; gx < ATLAS_GRID; gx++) {
      const ox = gx * tile;
      const oy = gy * tile;

      // Bolt traverses each tile vertically with displacement. Quad
      // rotation is per-instance, so the bolt's effective direction
      // varies regardless of the texture's vertical layout.
      const margin = tile * 0.12;
      const start = [ox + tile / 2 + (Math.random() - 0.5) * tile * 0.1, oy + margin];
      const end   = [ox + tile / 2 + (Math.random() - 0.5) * tile * 0.15, oy + tile - margin];

      // Generate the fractal polyline once, then stroke it three
      // times with different sizes + shadows for the layered glow.
      const segs = generateBoltSegments(start, end, 7, tile * 0.10);

      // Outer purple bloom. Wide, heavily blurred.
      ctx.lineWidth = tile * 0.045;
      ctx.strokeStyle = 'rgba(110, 50, 220, 0.55)';
      ctx.shadowBlur = tile * 0.10;
      ctx.shadowColor = 'rgba(140, 70, 240, 0.9)';
      strokeSegments(ctx, segs);

      // Mid cyan halo.
      ctx.lineWidth = tile * 0.018;
      ctx.strokeStyle = 'rgba(140, 220, 255, 0.95)';
      ctx.shadowBlur = tile * 0.05;
      ctx.shadowColor = 'rgba(160, 230, 255, 0.95)';
      strokeSegments(ctx, segs);

      // Hot white core. Thin, sharp.
      ctx.lineWidth = tile * 0.006;
      ctx.strokeStyle = 'rgba(255, 255, 255, 1.0)';
      ctx.shadowBlur = tile * 0.018;
      ctx.shadowColor = 'rgba(255, 255, 255, 1.0)';
      strokeSegments(ctx, segs);
    }
  }

  // Reset shadow so it doesn't bleed into any subsequent ctx use.
  ctx.shadowBlur = 0;
  ctx.shadowColor = 'transparent';

  const tex = new THREE.CanvasTexture(canvas);
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = 4;
  tex.needsUpdate = true;
  _sharedBoltAtlas = tex;
  return tex;
}

// ── Base-glow shader (subtle persistent purple) ──────────────────

const GLOW_VERT = /* glsl */ `
  varying vec2 vLocalXY;
  void main() {
    vLocalXY = position.xy;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const GLOW_FRAG = /* glsl */ `
  precision highp float;

  uniform float uTime;
  uniform vec2  uCardSize;
  uniform vec3  uColorGlow;

  varying vec2 vLocalXY;

  float hash(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }
  float vnoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(
      mix(hash(i),                hash(i + vec2(1.0, 0.0)), u.x),
      mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), u.x),
      u.y
    );
  }

  void main() {
    vec2 p = vLocalXY;

    // Card-silhouette clip.
    vec2 q = abs(p) - uCardSize * 0.5;
    float cardSdf = length(max(q, vec2(0.0))) + min(max(q.x, q.y), 0.0);
    float cardClip = smoothstep(0.1, -0.1, cardSdf);
    if (cardClip < 0.005) discard;

    // Subtle slow shimmer so the glow isn't a flat fill.
    float n = vnoise(p * 0.3 + vec2(uTime * 0.1, uTime * 0.07));
    // Slow pulse. The card is "charging up" between bolt flashes.
    float pulse = 0.6 + 0.4 * sin(uTime * 1.4 + n * 4.0);
    float intensity = (0.10 + 0.06 * n) * pulse * cardClip;

    if (intensity < 0.004) discard;
    gl_FragColor = vec4(uColorGlow * intensity, intensity);
  }
`;

// ── Bolt-instance shader ─────────────────────────────────────────

const BOLT_VERT = /* glsl */ `
  uniform float uTime;
  uniform float uPeriod;
  uniform float uLifetime;

  attribute vec2  aSpawn;
  attribute float aRotation;
  attribute float aScale;
  attribute float aPhase;
  attribute float aAtlasIdx; // 0..(ATLAS_GRID*ATLAS_GRID - 1)
  attribute float aSeed;

  varying vec2  vUv;
  varying float vAlpha;

  void main() {
    // Per-instance flash cycle: each bolt fires once per uPeriod,
    // staggered by aPhase. life ∈ [0, 1] within the period.
    float cycleT = mod(uTime + aPhase * uPeriod, uPeriod);
    float aliveFrac = uLifetime / uPeriod;

    if (cycleT > uLifetime) {
      // Outside the flash window. Collapse the vertex to the
      // origin so the bolt isn't visible (fragment shader will
      // discard via vAlpha=0 too).
      vAlpha = 0.0;
      gl_Position = vec4(0.0, 0.0, 2.0, 1.0); // off-screen
      return;
    }

    // Brightness curve: instant peak, sharp decay.
    float lifeT = cycleT / uLifetime;
    vAlpha = pow(1.0 - lifeT, 1.8);

    // Build the billboard quad in card-local space.
    float c = cos(aRotation);
    float s = sin(aRotation);
    vec2 corner = vec2(
      c * position.x - s * position.y,
      s * position.x + c * position.y
    ) * aScale;

    vec3 worldPos = vec3(aSpawn + corner, position.z);
    vec4 mvPos = modelViewMatrix * vec4(worldPos, 1.0);
    gl_Position = projectionMatrix * mvPos;

    // Atlas UV remap. Each tile occupies a 1/ATLAS_GRID region of
    // the texture.
    float grid = ${ATLAS_GRID.toFixed(1)};
    float idx = mod(aAtlasIdx, grid * grid);
    float tx = mod(idx, grid);
    float ty = floor(idx / grid);
    vUv = (uv + vec2(tx, ty)) / grid;
  }
`;

const BOLT_FRAG = /* glsl */ `
  precision highp float;

  uniform sampler2D uAtlas;

  varying vec2  vUv;
  varying float vAlpha;

  void main() {
    if (vAlpha < 0.003) discard;
    vec4 tex = texture2D(uAtlas, vUv);
    float a = tex.a * vAlpha;
    if (a < 0.003) discard;
    // Additive output. Bolts are bright light emitting features.
    gl_FragColor = vec4(tex.rgb * vAlpha, a);
  }
`;

export function createChargeLightningEffect() {
  const group = new THREE.Group();
  group.frustumCulled = false;

  // ── Base glow (subtle persistent purple) ──
  const glowGeometry = new THREE.PlaneGeometry(CARD_WIDTH * 1.05, CARD_HEIGHT * 1.05);
  const glowMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: Math.random() * 100 },
      uCardSize: { value: new THREE.Vector2(CARD_WIDTH, CARD_HEIGHT) },
      uColorGlow: { value: new THREE.Color(0x6028a0) },
    },
    vertexShader: GLOW_VERT,
    fragmentShader: GLOW_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });
  const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
  glowMesh.frustumCulled = false;
  glowMesh.position.z = CARD_THICKNESS / 2 + RISE_HEIGHT_OFFSET;
  glowMesh.renderOrder = 18;
  group.add(glowMesh);

  // ── Bolt instances ──
  const boltQuad = new THREE.PlaneGeometry(BOLT_SIZE, BOLT_SIZE);
  const boltGeometry = new THREE.InstancedBufferGeometry();
  boltGeometry.index = boltQuad.index;
  boltGeometry.setAttribute('position', boltQuad.getAttribute('position'));
  boltGeometry.setAttribute('uv', boltQuad.getAttribute('uv'));
  boltQuad.dispose();

  const spawns    = new Float32Array(BOLT_COUNT * 2);
  const rotations = new Float32Array(BOLT_COUNT);
  const scales    = new Float32Array(BOLT_COUNT);
  const phases    = new Float32Array(BOLT_COUNT);
  const atlasIdx  = new Float32Array(BOLT_COUNT);
  const seeds     = new Float32Array(BOLT_COUNT);

  for (let i = 0; i < BOLT_COUNT; i++) {
    spawns[i * 2]     = (Math.random() * 2 - 1) * BOLT_SPAWN_RADIUS_X;
    spawns[i * 2 + 1] = (Math.random() * 2 - 1) * BOLT_SPAWN_RADIUS_Y;
    rotations[i] = Math.random() * Math.PI * 2;
    // Per-instance scale variance so bolts look different sizes.
    scales[i] = 0.55 + Math.random() * 0.7;
    // Phases distributed evenly + jittered so the flashes don't
    // sync but also don't all bunch up.
    phases[i] = (i / BOLT_COUNT) + (Math.random() - 0.5) * 0.05;
    atlasIdx[i] = Math.floor(Math.random() * (ATLAS_GRID * ATLAS_GRID));
    seeds[i] = Math.random();
  }

  boltGeometry.setAttribute('aSpawn',    new THREE.InstancedBufferAttribute(spawns, 2));
  boltGeometry.setAttribute('aRotation', new THREE.InstancedBufferAttribute(rotations, 1));
  boltGeometry.setAttribute('aScale',    new THREE.InstancedBufferAttribute(scales, 1));
  boltGeometry.setAttribute('aPhase',    new THREE.InstancedBufferAttribute(phases, 1));
  boltGeometry.setAttribute('aAtlasIdx', new THREE.InstancedBufferAttribute(atlasIdx, 1));
  boltGeometry.setAttribute('aSeed',     new THREE.InstancedBufferAttribute(seeds, 1));
  boltGeometry.instanceCount = BOLT_COUNT;

  const boltMaterial = new THREE.ShaderMaterial({
    uniforms: {
      uTime: { value: glowMaterial.uniforms.uTime.value },
      uPeriod: { value: BOLT_PERIOD },
      uLifetime: { value: BOLT_LIFETIME },
      uAtlas: { value: getBoltAtlas() },
    },
    vertexShader: BOLT_VERT,
    fragmentShader: BOLT_FRAG,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
  });
  const boltMesh = new THREE.Mesh(boltGeometry, boltMaterial);
  boltMesh.frustumCulled = false;
  boltMesh.position.z = CARD_THICKNESS / 2 + RISE_HEIGHT_OFFSET + 0.04;
  boltMesh.renderOrder = 24;
  group.add(boltMesh);

  return {
    points: group,
    materials: [glowMaterial, boltMaterial],
    glowGeometry,
    boltGeometry,
  };
}

const _tmpVec3 = new THREE.Vector3();
const _tmpEuler = new THREE.Euler();
export function updateChargeLightningEffect(effect, dt, cardMesh) {
  if (!effect || !cardMesh) return;

  for (const material of effect.materials) {
    material.uniforms.uTime.value += dt;
  }

  cardMesh.getWorldPosition(_tmpVec3);
  effect.points.position.copy(_tmpVec3);

  const intent = cardMesh.userData?.intentEuler;
  const yaw = intent ? intent.z : 0;
  _tmpEuler.set(-Math.PI / 2, 0, yaw, 'XYZ');
  effect.points.rotation.copy(_tmpEuler);
}

export function disposeChargeLightningEffect(effect) {
  if (!effect) return;
  effect.points.parent?.remove(effect.points);
  effect.glowGeometry?.dispose();
  effect.boltGeometry?.dispose();
  for (const material of effect.materials || []) {
    material.dispose();
  }
}
